import type { ThemeClasses } from "../../types/components";
import {
  TEAM_MEMBER_ROLES,
  TEAM_MEMBER_STATUSES,
  PAY_MODES,
  MEMBER_PAY_MODES,
  PRICING_TYPES,
  SETTLEMENT_STATUSES,
  CONTRACTOR_ENGAGEMENT_STATUSES,
  SETTLEMENT_SUBJECTS,
  PAY_FREQUENCIES,
} from "@torre/team";

// themeClasses estándar compartido por la mini-app de Colaboradores (mismo shape
// que el usado por el Modal base y las demás secciones — soporta dark mode).
export const buildThemeClasses = (isDarkMode: boolean): ThemeClasses => ({
  bg: isDarkMode ? "bg-dark-surface" : "bg-white",
  border: isDarkMode ? "border-dark-border" : "border-gray-200",
  text: isDarkMode ? "text-white" : "text-gray-900",
  textSecondary: isDarkMode ? "text-gray-400" : "text-gray-600",
  cardBg: isDarkMode ? "bg-dark-card" : "bg-white",
  cardBorder: isDarkMode ? "border-dark-border" : "border-gray-200",
  buttonHover: isDarkMode ? "hover:bg-dark-card" : "hover:bg-gray-100",
  buttonText: isDarkMode ? "text-gray-400" : "text-gray-600",
  buttonTextHover: isDarkMode ? "hover:text-white" : "hover:text-gray-900",
});

// --- Vocabularios + etiquetas en español (en sync con @torre/team) ---
export const ROLE_OPTIONS = TEAM_MEMBER_ROLES;
export const TEAM_MEMBER_STATUS_OPTIONS = TEAM_MEMBER_STATUSES;
export const MODE_OPTIONS = PAY_MODES;
export const MEMBER_PAY_MODE_OPTIONS = MEMBER_PAY_MODES;
export const PRICING_TYPE_OPTIONS = PRICING_TYPES;
export const SETTLEMENT_STATUS_OPTIONS = SETTLEMENT_STATUSES;
export const STATUS_ENGAGEMENT_OPTIONS = CONTRACTOR_ENGAGEMENT_STATUSES;
export const SUBJECT_OPTIONS = SETTLEMENT_SUBJECTS;

/**
 * Firma mínima de la `t` de react-i18next.
 *
 * Las etiquetas de los vocabularios (rol, modalidad, estado…) NO viven en
 * español fijo: se resuelven contra el namespace `common` igual que el resto
 * de la mini-app. Un usuario en inglés veía antes la interfaz traducida pero
 * los badges y los selectores en español.
 */
export type TranslateFn = (key: string, options?: Record<string, unknown>) => string;

/**
 * Construye el resolvedor de etiquetas de un vocabulario.
 *
 * - valor ausente/vacío → "—" (no hay dato, no se inventa uno).
 * - código sin traducción → el propio código (nunca una celda vacía).
 */
const vocabulary =
  (prefix: string) =>
  (t: TranslateFn, code: unknown): string => {
    const raw = code === null || code === undefined ? "" : String(code).trim();
    if (raw === "") return "—";
    return t(`${prefix}.${raw}`, { defaultValue: raw });
  };

/** Rol del colaborador (AgroCloud#11). */
export const roleLabel = vocabulary("team.roles");
/** Estado laboral del colaborador (AgroCloud#11). */
export const teamMemberStatusLabel = vocabulary("team.memberStatuses");
/** Modalidad de pago de la tarifa/colaborador (#530). */
export const modeLabel = vocabulary("team.modes");
/** Tipo de precio de una tarifa por entregable (AgroCloud#13). */
export const pricingTypeLabel = vocabulary("team.pricingTypes");
/** Estado de una liquidación de equipo (AgroCloud#13). */
export const settlementStatusLabel = vocabulary("team.settlementStatuses");
/** Estado de una tarea/servicio de contratista (AgroCloud#60). */
export const engagementStatusLabel = vocabulary("team.engagementStatuses");
/** Sujeto de la liquidación: colaborador o squad (AgroCloud#12/#13). */
export const subjectLabel = vocabulary("team.subjects");

export const ROLE_TONES: Record<string, string> = {
  developer: "bg-emerald-500/15 text-emerald-500",
  tech_lead: "bg-primary-500/15 text-primary-500",
  engineering_manager: "bg-purple-500/15 text-purple-500",
  scrum_master: "bg-indigo-500/15 text-indigo-500",
  product_owner: "bg-pink-500/15 text-pink-500",
  qa_engineer: "bg-teal-500/15 text-teal-500",
  devops_engineer: "bg-amber-500/15 text-amber-500",
  designer: "bg-rose-500/15 text-rose-500",
  sales: "bg-orange-500/15 text-orange-500",
  marketing: "bg-fuchsia-500/15 text-fuchsia-500",
  freelancer: "bg-sky-500/15 text-sky-500",
  administrative: "bg-gray-400/15 text-gray-500",
};

export const TEAM_MEMBER_STATUS_TONES: Record<string, string> = {
  active: "bg-emerald-500/15 text-emerald-500",
  inactive: "bg-gray-400/15 text-gray-500",
  offboarded: "bg-red-500/15 text-red-500",
};

export const MODE_TONES: Record<string, string> = {
  hourly: "bg-indigo-500/15 text-indigo-500",
  daily: "bg-sky-500/15 text-sky-500",
  deliverable: "bg-amber-500/15 text-amber-500",
  project: "bg-purple-500/15 text-purple-500",
  fixed_salary: "bg-emerald-500/15 text-emerald-500",
};

/** Qué cantidad y unidad resume una liquidación, según su modalidad. */
export function settlementQuantity(r: Record<string, unknown>): string {
  switch (r.mode) {
    case "hourly":
      return `${String(r.hours ?? 0)} h`;
    case "daily":
      return `${String(r.days ?? 0)} d`;
    case "project":
      return `${String(r.progressPercentage ?? 0)} %`;
    case "fixed_salary":
      return `${String(r.periods ?? 1)} per.`;
    default:
      return String(r.output ?? 0);
  }
}

export const SETTLEMENT_STATUS_TONES: Record<string, string> = {
  draft: "bg-gray-400/15 text-gray-500",
  calculated: "bg-sky-500/15 text-sky-500",
  approved: "bg-amber-500/15 text-amber-500",
  paid: "bg-emerald-500/15 text-emerald-500",
  void: "bg-red-500/15 text-red-500",
};

export const STATUS_ENGAGEMENT_TONES: Record<string, string> = {
  planned: "bg-gray-400/15 text-gray-500",
  in_progress: "bg-amber-500/15 text-amber-500",
  completed: "bg-sky-500/15 text-sky-500",
  settled: "bg-emerald-500/15 text-emerald-500",
  void: "bg-red-500/15 text-red-500",
};

/**
 * Formatea un monto como moneda local (sin depender del locale del navegador).
 *
 * Un monto ausente NO es 0: se muestra "—". Pintar "MXN 0" donde no hay dato
 * hace creer que a alguien no se le debe nada cuando en realidad no se sabe.
 *
 * El parámetro es `unknown` a propósito: las filas de la tabla llegan como
 * `Record<string, unknown>` y el valor debe pasarse TAL CUAL. Envolverlo en
 * `Number(...)` en cada llamada era el bug: `Number(null) === 0` convertía
 * "no hay precio" en "cuesta 0", justo lo que esta función existe para evitar.
 */
export function formatMoney(amount: unknown, currency: string | null = "MXN"): string {
  if (amount === null || amount === undefined || amount === "") return "—";
  const n = Number(amount);
  if (!Number.isFinite(n)) return "—";
  // Sin moneda declarada se muestra el importe «pelón» en vez de etiquetarlo
  // como pesos: suponer la moneda de alguien es equivocarse en su sueldo (#741).
  const code = String(currency ?? "").trim().toUpperCase();
  const monto = n.toLocaleString("es-MX", { maximumFractionDigits: 2 });
  return /^[A-Z]{3}$/.test(code) ? `${code} ${monto}` : monto;
}

/** Monedas que se ofrecen en el formulario. Es una lista corta a propósito:
 * son las que la empresa usa hoy, y el campo acepta cualquier código ISO. */
export const CURRENCY_OPTIONS = ["MXN", "USD"] as const;

/** Periodicidades de un sueldo fijo (#1296), en el orden del formulario. */
export const PAY_FREQUENCY_OPTIONS = PAY_FREQUENCIES;
/**
 * Etiqueta de la periodicidad («por quincena», «al mes»…). Reutiliza el
 * vocabulario de la columna Sueldo para que tarifa y padrón digan lo mismo.
 */
export const payFrequencyLabel = vocabulary("team.salary.per");
